import { useState, useCallback, useEffect } from 'react';
import Main from '../components/layouts/Main';
import { Spin, Button, Tooltip, Select } from 'antd';
import { useUserContext } from '../contexts/UserContext';
import MainFeed from '../components/dashboard/MainFeed';
import FeedFilterBar from '../components/dashboard/FeedFilterBar';
import { PrioriteService } from '../services/PrioriteService';
import { StatutService } from '../services/StatutService';
import { catchError } from '../services/DaoService';
import {
  AlertOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import styles from './FilActualitePage.module.scss';
import PropTypes from 'prop-types';

const greetingByHour = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

const formatDate = () =>
  new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

function StatChip({ icon, value, label, danger, loading }) {
  return (
    <div className={`${styles.statChip} ${danger ? styles.statChipDanger : ''}`}>
      {loading
        ? <div className={styles.statSkeleton} />
        : <>
            <span className={styles.statIcon}>{icon}</span>
            <span className={styles.statBody}>
              <span className={styles.statValue}>{value ?? '—'}</span>
              <span className={styles.statLabel}>{label}</span>
            </span>
          </>
      }
    </div>
  );
}

export default function FilActualitePage() {
  const { currentUser } = useUserContext();
  const [stats, setStats] = useState(null);
  const [feedKey, setFeedKey] = useState(0);
  const [filters, setFilters] = useState({ my_role: null });
  const [assignes, setAssignes] = useState([]);
  const [priorites, setPriorites] = useState([]);
  const [statuts, setStatuts] = useState([]);

  useEffect(() => {
    PrioriteService.getInstance().all().then(r => setPriorites(r.data ?? [])).catch(catchError);
    StatutService.getInstance().all().then(r => setStatuts(r.data ?? [])).catch(catchError);
  }, []);

  const handleStats = useCallback((s) => setStats(s), []);
  const handleAssignes = useCallback((a) => setAssignes(a ?? []), []);
  const refresh = () => { setStats(null); setFeedKey(k => k + 1); };

  if (!currentUser) return <Spin size="large" fullscreen />;

  const firstName = currentUser.name?.split(' ')[0] ?? currentUser.name;

  return (
    <Main>
      {/* ── Header ─────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.greeting}>{greetingByHour()}</span>
          <span className={styles.userName}>{firstName}</span>
          <span className={styles.headerMeta}>
            Tableau de bord · Supervision globale &nbsp;·&nbsp; {formatDate()}
          </span>
        </div>

        <div className={styles.headerRight}>
          <StatChip
            icon={<AlertOutlined />}
            value={stats?.retard}
            label="En retard"
            danger={stats?.retard > 0}
            loading={!stats}
          />
          <StatChip
            icon={<ThunderboltOutlined />}
            value={stats?.actifs}
            label="En cours"
            loading={!stats}
          />
          <StatChip
            icon={<CheckCircleOutlined />}
            value={stats?.termines}
            label="Terminés"
            loading={!stats}
          />
        </div>
      </div>

      {/* ── Feed header ────────────────────────────────────── */}
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          <AppstoreOutlined />
          Mes activités
          {stats && (
            <span className={styles.sectionCount}>
              {stats.actifs + stats.termines} éléments
            </span>
          )}
        </span>
        <Tooltip title="Rafraîchir">
          <Button size="small" icon={<ReloadOutlined />} onClick={refresh}>
            Rafraîchir
          </Button>
        </Tooltip>
      </div>

      {/* ── Role filter ────────────────────────────────────── */}
      <div style={{ padding: '8px 0 4px' }}>
        <Select
          allowClear
          placeholder="Mon rôle"
          value={filters.my_role ?? undefined}
          onChange={v => setFilters(f => ({ ...f, my_role: v ?? null }))}
          style={{ width: 220 }}
          options={[
            { value: 'assigned_by_me', label: "Que j'ai assigné" },
            { value: 'responsable', label: 'Dont je suis responsable' },
            { value: 'assigned_to_me', label: "Qu'on m'a assigné" },
            { value: 'collaborateur', label: 'Simple collaborateur' },
          ]}
        />
      </div>

      {/* ── Filters ────────────────────────────────────────── */}
      <FeedFilterBar
        filters={filters}
        onChange={setFilters}
        assignes={assignes}
        priorites={priorites}
        statuts={statuts}
      />

      {/* ── Combined feed ──────────────────────────────────── */}
      <MainFeed key={feedKey} filters={filters} onStats={handleStats} onAssignes={handleAssignes} />
    </Main>
  );
}

StatChip.propTypes = {
  icon: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string.isRequired,
  danger: PropTypes.bool,
  loading: PropTypes.bool,
};